import { Editor } from '@tiptap/core';
import { cloneElement, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, HStack, rem, Stack, Text } from '@bonusly/design-system';

import { SlashCommandOption } from './types';

type SlashMenuProps = {
	command: (option: SlashCommandOption) => void;
	editor: Editor;
	items: SlashCommandOption[];
};

export const SlashMenu = forwardRef(({ command, items }: SlashMenuProps, ref) => {
	const { t } = useTranslation('one_on_ones');
	const scrollContainer = useRef<HTMLDivElement>(null);
	const activeItem = useRef<HTMLButtonElement>(null);
	const [selectedIndex, setSelectedIndex] = useState(0);

	const selectItem = useCallback(
		(index: number) => {
			const option = items[index];
			command(option);
		},
		[command, items]
	);

	useImperativeHandle(ref, () => ({
		onKeyDown: ({ event }: { event: KeyboardEvent }) => {
			if (event.key === 'ArrowDown') {
				if (!items.length) {
					return false;
				}

				const newIndex = selectedIndex + 1;

				if (items.length - 1 < newIndex) {
					setSelectedIndex(0);
				} else {
					setSelectedIndex(newIndex);
				}

				return true;
			}

			if (event.key === 'ArrowUp') {
				if (!items.length) {
					return false;
				}

				const newIndex = selectedIndex - 1;

				if (newIndex < 0) {
					setSelectedIndex(items.length - 1);
				} else {
					setSelectedIndex(newIndex);
				}

				return true;
			}

			if (event.key === 'Enter') {
				if (!items.length || selectedIndex === -1) {
					return false;
				}

				selectItem(selectedIndex);

				return true;
			}

			return false;
		},
	}));

	useEffect(() => {
		if (activeItem.current && scrollContainer.current) {
			const offsetTop = activeItem.current.offsetTop;
			const offsetHeight = activeItem.current.offsetHeight;

			scrollContainer.current.scrollTop = offsetTop - offsetHeight;
		}
	}, [selectedIndex]);

	return (
		<Box ref={scrollContainer} bg="bg.surface" p={1} borderRadius="md" boxShadow="md" minW={rem(210)}>
			<Stack w="full" gap={1}>
				{items.map((command, index) => {
					const isOptionDisabled = !command.name || !command.icon;
					return (
						<HStack
							as="button"
							borderRadius="sm"
							p={isOptionDisabled ? 0 : 2}
							pointerEvents={isOptionDisabled ? 'none' : 'auto'}
							gap={2}
							key={command.name || 'cmd-' + index}
							transitionProperty="background-color"
							transitionDuration="normal"
							color="text.default"
							_active={{ bg: 'secondary.bgAlpha' }}
							_hover={{ bg: 'secondary.bgAlpha' }}
							onClick={() => selectItem(index)}
							{...(index === selectedIndex && {
								'data-active': true,
							})}
						>
							{isOptionDisabled ? <Box w="100%" borderTop="1px solid" borderColor="border.default" /> : null}
							{command.icon && cloneElement(command.icon, { size: 'sm' })}
							{command.key ? <Text>{t(`editor.${command.key}`)}</Text> : command.label ? <Text>{command.label}</Text> : null}
						</HStack>
					);
				})}
			</Stack>
		</Box>
	);
});

SlashMenu.displayName = 'SlashMenu';
